<!--
* 组件名 : app
* 开发者 : XJ
*
-->
<template>
  <v-app>
    <template v-if="firstLoadingDone">
      <headerNav />
      <v-main><routeViewBox /></v-main>
      <v-batch-operation v-if="selectedCards.length > 0" />
      <v-backtop />
      <!-- 全局高质感轻量 Toast 胶囊提示 -->
      <transition name="toast-slide">
        <div
          v-if="toast.show"
          class="global-toast-capsule"
          :class="'type-' + toast.type"
        >
          <v-icon small class="toast-icon">{{ toast.icon }}</v-icon>
          <span class="toast-text">{{ toast.text }}</span>
        </div>
      </transition>
    </template>
    <template v-else>
      <v-loading />
    </template>
  </v-app>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import { get_api_data } from "@/utils/ajax";
import headerNav from "./components/header-nav/index";
import routeViewBox from "./components/route-view-box/index.vue";
import activityTracker from "@/utils/activityTracker";

export default {
  name: "App",
  components: {
    headerNav,
    routeViewBox,
  },
  data() {
    return {
      toast: {
        show: false,
        text: "",
        type: "success",
        icon: "mdi-check-circle",
      },
      toastTimer: null,
    };
  },
  computed: {
    ...mapState("global", ["firstLoadingDone", "churchLifeModel"]),
    ...mapState("batchOperation", ["selectedCards"]),
  },
  mounted() {
    // 根节点优先恢复本地持久化完整菜单缓存，使左侧导航树实现 0ms 秒开渲染
    try {
      const cachedMenu = localStorage.getItem("material_libraries_menu_cache_v4");
      if (cachedMenu && (!this.$store.state.global.navInfos || this.$store.state.global.navInfos.length === 0)) {
        const parsedMenu = JSON.parse(cachedMenu);
        if (Array.isArray(parsedMenu) && parsedMenu.length > 0 && parsedMenu.some(item => item.lv1_categories && item.lv1_categories.length > 0)) {
          this.$store.commit("global/updateNavInfos", parsedMenu);
        }
      }
    } catch (e) {}
    this.$P.$on("show_toast", ({ text, type = "success" }) => {
      this.triggerToast(text, type);
    });
    this.$root.$toast = this.triggerToast;
    this.load_data();
    // 启动 120 分钟用户无操作自动退出守卫
    activityTracker.start();
  },
  beforeDestroy() {
    activityTracker.stop();
  },
  methods: {
    ...mapMutations("global", [
      "updateAllCardList",
      "updateAllCardListMeta",
      "updateFirstLoadingDone",
      "updateChurchLifeModel",
      "updateIsListLoading",
    ]),

    triggerToast(text, type = "success") {
      clearTimeout(this.toastTimer);
      let icon = "mdi-check-circle";
      if (type === "info") icon = "mdi-information";
      if (type === "warning") icon = "mdi-alert-circle";
      if (type === "error") icon = "mdi-close-circle";
      this.toast = {
        show: true,
        text,
        type,
        icon,
      };
      this.toastTimer = setTimeout(() => {
        this.toast.show = false;
      }, 2400);
    },

    async load_data() {
      const isSubCategory = Boolean(this.$route.params.categories && this.$route.params.materialList);
      const isDriveOnly = Boolean(this.$route.params.categories && !this.$route.params.materialList);
      const isHome = !this.$route.params.categories;
      const isChurchLife = this.$route.params.categories === "0AChSkKAoqzZMUk9PVA";
      isChurchLife ? this.updateChurchLifeModel(0) : this.updateChurchLifeModel("");

      const driveIds = isHome ? [] : [this.$route.params.categories];
      const lv1_categories = isSubCategory ? [this.$route.params.materialList] : [];
      const cacheKey = isHome
        ? 'home_all_latest'
        : (isDriveOnly ? `${driveIds[0]}_all_${this.churchLifeModel || 0}` : `${driveIds[0]}_${lv1_categories[0]}_${this.churchLifeModel || 0}`);

      this.updateIsListLoading(true);
      this.updateAllCardListMeta({ total: null });
      try {
        let firstLoadingData = await get_api_data("GET", "search", {
          driveIds,
          lv1_categories,
          lv2_categories: [],
          type: "all",
          start: "",
          end: "",
          keyword: "",
          scenes: this.churchLifeModel || 0,
          inc_tag: "include",
          orderby: "created_at",
          order: "desc",
          per_page: 80,
          page: 1,
        });

        if (firstLoadingData && firstLoadingData.data) {
          this.updateAllCardList(firstLoadingData.data.data);
          this.updateAllCardListMeta(firstLoadingData.data.meta);
        }
      } catch (err) {
        console.error("首屏加载最新素材异常:", err);
      } finally {
        this.updateIsListLoading(false);
        this.updateFirstLoadingDone();
      }
    },
  },
};
</script>

<style lang="scss">
.global-toast-capsule {
  position: fixed;
  top: 96px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999999;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(16, 185, 129, 0.35);
  box-shadow: 0 10px 28px -4px rgba(13, 148, 136, 0.28),
              0 2px 8px -2px rgba(0, 0, 0, 0.06);
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  pointer-events: none;

  .toast-icon {
    font-size: 18px !important;
  }

  &.type-success {
    border-color: rgba(16, 185, 129, 0.45);
    .toast-icon {
      color: #10b981 !important;
    }
  }

  &.type-info {
    border-color: rgba(56, 189, 248, 0.45);
    .toast-icon {
      color: #0284c7 !important;
    }
  }

  &.type-warning {
    border-color: rgba(245, 158, 11, 0.45);
    .toast-icon {
      color: #d97706 !important;
    }
  }

  &.type-error {
    border-color: rgba(239, 68, 68, 0.45);
    .toast-icon {
      color: #ef4444 !important;
    }
  }
}

.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-slide-enter {
  opacity: 0;
  transform: translate(-50%, -18px) scale(0.92);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, -12px) scale(0.95);
}
</style>
